/**
 * The round-steps checklist (the rail's guide): every step of a round in order, each done, current or next, from the state the sticky
 * bar already reads (`nextStep`, the draft version, the forecast, the submission) and the round (solo: `soloPhase`, the solo hub's phase).
 * The current step is the sticky bar's first unmet item while the team is deciding, so the two never disagree.
 */
import type { TeamState } from "@bsim/contracts";
import { nextStep, type NextStepKind } from "../decisions/nextStep";

type RoundKey = "results" | "brief" | "outlook" | "call" | "levers" | "rationales" | "events" | "thesis" | "review" | "sign" | "runs" | "outcome";
export type GuideKey = RoundKey | "strategy" | "risk" | "practice";
/** `optional`: nothing to do here this round (no lever changed), so it is neither done nor waiting. */
export type GuideStatus = "done" | "current" | "next" | "skipped" | "optional";
/** `counted`: the step is still in front of the player (a new change needs its reason) but was already done once this round, so the count never steps back; the "due" part is its hint, a pending item beside the count. */
export interface GuideStep { readonly key: GuideKey; readonly label: string; readonly href: string; readonly status: GuideStatus; readonly hint: string | null; readonly counted?: boolean }
export interface RoundGuide { readonly steps: readonly GuideStep[]; readonly current: number }

/** Take the Chair (round 0): its three steps, which the chair page keeps in `?step=`. */
export const CHAIR_STEPS = ["Strategy", "Risk appetite", "Practice quarter"] as const;
/** The page after the three steps: "Your bank, on its standing policy", the one calm page between the practice and round 1. It is a step of the address (`?step=3`), not one of the pills. */
export const CHAIR_INTRO_STEP = CHAIR_STEPS.length;
export const chairStepOf = (search: string): number => Math.min(CHAIR_INTRO_STEP, Math.max(0, Number(new URLSearchParams(search).get("step")) || 0));

/** The review step opens the Review page at the risk officer's memo (the section `id="memo"`), not at its top. */
export const MEMO_HASH = "#memo";

const ORDER: readonly RoundKey[] = ["results", "brief", "outlook", "call", "levers", "rationales", "events", "thesis", "review", "sign", "runs", "outcome"];
/** The twin call opens a round from the second year on. */
const FIRST_CALL_ROUND = 2;

type Stage = "waiting" | "deciding" | "running" | "results" | "done";

function stageOf(team: TeamState): Stage {
  const round = team.round;
  if (round === null) return "waiting";
  if (round.soloPhase !== null) return ({ deciding: "deciding", resolving: "running", awaiting_event: "running", reviewing: "results", completed: "done" } as const)[round.soloPhase];
  switch (round.state === "paused" ? round.pausedFrom ?? "open" : round.state) {
    case "draft": return "waiting";
    case "open": return "deciding";
    case "released": return "results";
    default: return "running";
  }
}

const KEY_OF_STEP: Readonly<Record<Exclude<NextStepKind, "review">, RoundKey>> = { call: "call", rationale: "rationales", event: "events", thesis: "thesis" };

/** Where the team is in the round, as the key of the step in front of it (before resolving to a step the round actually has). */
function currentKey(team: TeamState, stage: Stage, hasResults: boolean, outlookOpened: boolean): RoundKey {
  const draft = team.draft;
  switch (stage) {
    case "waiting": return hasResults ? "results" : "brief";
    case "running": return "runs";
    case "results": case "done": return "outcome";
    case "deciding": {
      // "CEO signs" is done by the team's own submission; a rescue or a deadline close of the same version leaves it open. A signed plan is past every earlier step, the outlook included.
      const own = team.latestSubmission?.source === "team" || team.latestSubmission?.source === "solo";
      if (draft !== null && own && team.latestSubmission?.draftVersion === draft.version) return "runs";
      // The outlook comes before the decisions: until it has been opened this round it is the step in front of the player.
      if (!outlookOpened) return "outlook";
      const next = nextStep(team);
      if (next.kind !== "review") return KEY_OF_STEP[next.kind];
      const reviewed = team.cohort.mode === "solo" ? team.croMemo?.acknowledgedAt != null : team.croAttestation?.isCurrent === true;
      return reviewed ? "sign" : "review";
    }
  }
}

/** Round 0: Strategy → Risk appetite → Practice quarter, with `step` the one in front of the player. A step ticks only when what it holds was saved; passing it unsaved shows it as skipped. */
function chairGuide(cohortId: string, step: number, saved: { readonly strategy: boolean; readonly limits: boolean }): RoundGuide {
  const steps = CHAIR_STEPS.map((label, i): GuideStep => {
    const savedHere = i === 0 ? saved.strategy : i === 1 ? saved.limits : true;
    const skipped = i < step && !savedHere;
    return {
      key: (["strategy", "risk", "practice"] as const)[i] ?? "strategy", label, href: `/c/${cohortId}/chair?step=${i}`,
      status: skipped ? "skipped" : i < step ? "done" : i === step ? "current" : "next",
      hint: skipped ? (i === 0 ? "Skipped · add a line when you can" : "Not saved · the usual limits apply") : null,
    };
  });
  return { steps, current: Math.min(step, steps.length - 1) };
}

/** `chairStep` is the chair page's step while the player is on Take the Chair, null elsewhere: the chair has its own checklist until it is taken. */
/** `outlookOpened`: the Market Outlook has been opened this round (the rail dot's own marker); it ticks the "Outlook" step. */
/**
 * The guide step the page in front of the player is (null on a page that is no step of the round): the Outlook, the brief on the cockpit, last year's results, and on Decide the walk's
 * step from `?step=` (a decision is "Set your decisions"; the review is left to the checklist's own next step). Like every stepper, the guide marks the step the player is on as current
 * and the next undone one as next.
 */
export function viewedStepOf(pathname: string, search: string, roundNo: number): GuideKey | null {
  const [, , , page, arg] = pathname.split("/");
  if (page === "outlook") return "outlook";
  if (page === "cockpit") return "brief";
  if (page === "results") return Number(arg) === roundNo - 1 ? "results" : null;
  if (page !== "decide") return null;
  const params = new URLSearchParams(search);
  if (params.get("mode") === "review") return "review"; // the review page is the review step, whatever a skipped earlier step says
  const step = params.get("step") ?? "";
  return step.startsWith("r:") ? "rationales" : step === "cards" ? "events" : step === "thesis" ? "thesis" : step.startsWith("d:") ? "levers" : null;
}

export function roundGuide(team: TeamState, cohortId: string, chairStep: number | null = null, outlookOpened = true, viewed: GuideKey | null = null): RoundGuide | null {
  if (chairStep !== null) return chairGuide(cohortId, chairStep, { strategy: (team.strategyStatement?.text ?? "").trim() !== "" || (team.strategyStatement?.objectives.length ?? 0) > 0, limits: team.strategyStatement !== null && team.strategyStatement.confirmed !== false });
  const round = team.round;
  if (round === null || round.roundNo < 1) return null;
  const solo = team.cohort.mode === "solo";
  const base = `/c/${cohortId}`;
  const stage = stageOf(team);
  // The list is fixed when the round opens: a card due at the start of the year is a step of Decide; a card that arrives mid-year is never one (it pauses the year, and the run step says so).
  const startCards = team.pendingEvents.filter((e) => e.mode === "planned" && e.timing.phase === "roundStart");
  const planned = startCards.length > 0;
  const present = (k: RoundKey): boolean => k === "results" ? round.roundNo > 1 : k === "call" ? round.roundNo >= FIRST_CALL_ROUND : k === "events" ? planned : true;
  const wanted = ORDER.indexOf(currentKey(team, stage, present("results"), outlookOpened));
  const at = ORDER.findIndex((k, i) => i >= wanted && present(k));
  const review = `${base}/decide?mode=review`;
  // A card that stops the year is a different card from the one answered on Decide: the one run entry says it is paused, never a second step.
  const awaitingCard = solo && round.soloPhase === "awaiting_event";
  const catalog: Readonly<Record<RoundKey, { label: string; href: string }>> = {
    results: { label: "Results from last year", href: `${base}/results/${round.roundNo - 1}` },
    brief: { label: "Round brief", href: `${base}/cockpit` },
    outlook: { label: "Outlook", href: `${base}/outlook` },
    call: { label: "Your forecast", href: `${base}/decide` },
    levers: { label: "Set your decisions", href: `${base}/decide` },
    rationales: { label: "Write your rationales", href: `${base}/decide` },
    events: { label: "Answer the round-start card", href: `${base}/decide` },
    thesis: { label: "Thesis line", href: `${base}/decide?step=thesis#decide-thesis` },
    review: { label: solo ? "Risk officer's memo" : "Risk officer's review", href: `${review}${MEMO_HASH}` },
    sign: { label: solo ? "Review & submit" : "CEO signs", href: review },
    runs: { label: "Running the year", href: solo ? `/solo/${cohortId}` : `${base}/cockpit` },
    outcome: { label: "Results", href: stage === "done" ? `/solo/${cohortId}/final` : `${base}/results/${round.roundNo}` },
  };
  const hints: Partial<Record<RoundKey, string>> = {
    runs: stage === "running" ? (awaitingCard ? "Paused for a card: answer it to go on" : "Your year is running") : "Waiting for the round to close",
    outcome: solo && stage === "results" ? (round.roundNo >= team.cohort.roundCount ? "See your final" : "Continue to the next year") : "",
  };
  // While deciding, the step the page is on reads as current and the next undone step as next; the done count keeps what was done.
  const viewing = stage === "deciding" && viewed !== null && ORDER.indexOf(viewed as RoundKey) >= 0 && present(viewed as RoundKey) ? (viewed as RoundKey) : null;
  const steps = ORDER.filter(present).map((key): GuideStep => {
    const i = ORDER.indexOf(key);
    // An optional thesis (a lever changed) is done only when it was written; passed unwritten it stays open and says it is optional.
    const optionalThesis = key === "thesis" && team.draft !== null && !team.draft.thesis.required;
    const unwritten = optionalThesis && (team.draft?.thesis.text ?? "").trim() === "";
    // Levers and rationales are ticked only when a lever changed: with none changed they are optional, not done.
    const unchanged = (key === "levers" || key === "rationales") && team.draft !== null && team.draft.changedFamilies.length === 0;
    // A plan signed with no attestation did not have the risk officer's review: the step reads skipped, never done.
    const unreviewed = key === "review" && team.cohort.mode !== "solo" && team.latestSubmission?.attested === false;
    const status = unchanged && stage === "deciding" ? "optional" : unchanged && i < at ? "done" : unreviewed && i < at ? "skipped" : unwritten ? (i === at ? "current" : "next") : stage === "done" || i < at ? "done" : i === at ? "current" : "next";
    // A reason already written this round keeps the step counted when a further change needs one: the count never steps back for a change, and the reason due is a pending note beside it.
    const missing = team.draft?.missingRationales.length ?? 0;
    const written = key === "rationales" && status === "current" && stage === "deciding" && missing > 0 && (team.draft?.changedFamilies.length ?? 0) > missing;
    const hint = status === "optional" ? null : status === "skipped" ? "Signed without the risk officer" : written ? `${missing === 1 ? "1 reason" : `${missing} reasons`} still due` : status === "current" ? hints[key] || null : null;
    const here = viewing === key;
    const shown: GuideStatus = viewing === null ? status : here ? "current" : i === at ? "next" : status;
    const shownHint = shown === status ? hint : shown === "current" ? hints[key] || null : null;
    return { key, ...catalog[key], ...(optionalThesis ? { label: "Thesis line · optional" } : {}), status: shown, hint: shownHint, ...(written || (here && status === "done") ? { counted: true } : {}) };
  });
  return { steps, current: steps.findIndex((s) => s.status === "current") };
}
